import { Check } from 'lucide-react';
import { motion } from 'motion/react';
import { RadioGroup as RadioGroupPrimitive, Switch as SwitchPrimitive } from 'radix-ui';
import { type ComponentProps, type ReactNode, useId } from 'react';

import { cn } from '../lib/cn.ts';
import { TRANSITION } from '../lib/motion.tsx';
import { FieldErrorNote } from './checkbox.tsx';
import { Field, type FieldPart, type FieldSize, useFieldGroupControl } from './field.tsx';

/** Вариант выбора: значение, подпись, пояснение и значок. Общий для сегментов, карточек и списков. */
export interface ChoiceOption<V extends string = string> {
  readonly value: V;
  readonly label: string;
  /** Вторая строка: что значит вариант. */
  readonly description?: string;
  readonly icon?: ReactNode;
  /** Дополнительные слова для поиска по списку. */
  readonly keywords?: readonly string[];
  readonly disabled?: boolean;
}

const SEGMENT_SIZE: Record<FieldSize, string> = {
  sm: 'h-6 px-2 text-small',
  md: 'h-7 px-2.5 text-body',
  lg: 'h-9 px-3.5 text-lead',
};

export interface SegmentsProps<V extends string> {
  readonly value: V;
  readonly onValueChange: (value: V) => void;
  readonly options: readonly ChoiceOption<V>[];
  readonly size?: FieldSize;
  /** На всю ширину: сегменты делят её поровну. */
  readonly stretch?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

/**
 * Сегменты: выбор одного из 2–5 коротких вариантов, все видны сразу. Стрелки переключают
 * вариант, Tab уходит из группы.
 */
export function Segments<V extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  stretch = false,
  disabled,
  className,
  ...aria
}: SegmentsProps<V>) {
  const control = useFieldGroupControl();
  // Плашка выбранного сегмента переезжает к новому выбору.
  const thumb = useId();
  return (
    <RadioGroupPrimitive.Root
      {...control}
      {...aria}
      value={value}
      onValueChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onValueChange(option.value);
      }}
      disabled={disabled ?? control.disabled}
      orientation="horizontal"
      loop
      className={cn(
        'isolate inline-flex max-w-full gap-0.5 overflow-x-auto rounded-md bg-surface-2 p-0.5',
        stretch && 'flex w-full',
        className,
      )}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          title={option.description}
          className={cn(
            'relative inline-flex shrink-0 items-center justify-center rounded-sm font-semibold whitespace-nowrap text-muted',
            'transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            'data-[state=checked]:text-on-strong',
            'disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:size-4',
            stretch && 'flex-1',
            SEGMENT_SIZE[size],
          )}
        >
          {option.value === value ? (
            <motion.span
              aria-hidden="true"
              layoutId={thumb}
              transition={TRANSITION.layout}
              className="absolute inset-0 z-0 rounded-sm bg-strong"
            />
          ) : null}
          {/* Подпись над плашкой — и своей, и пролетающей мимо. */}
          <span className="relative z-10 inline-flex items-center gap-1.5">
            {option.icon}
            {option.label}
          </span>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}

export interface SegmentsFieldProps<V extends string>
  extends FieldPart, Omit<SegmentsProps<V>, 'disabled' | 'className'> {}

export function SegmentsField<V extends string>({
  value,
  onValueChange,
  options,
  size,
  stretch,
  ...field
}: SegmentsFieldProps<V>) {
  return (
    <Field {...field}>
      <Segments
        value={value}
        onValueChange={onValueChange}
        options={options}
        {...(size ? { size } : {})}
        {...(stretch ? { stretch } : {})}
      />
    </Field>
  );
}

export interface SwitchProps extends Omit<
  ComponentProps<typeof SwitchPrimitive.Root>,
  'checked' | 'onCheckedChange' | 'children'
> {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
}

/** Переключатель: действие срабатывает сразу (в отличие от флажка в форме с кнопкой «Сохранить»). */
export function Switch({ className, ...props }: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        'peer relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-thick border-muted bg-surface-2',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        'focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block size-3.5 translate-x-0.5 rounded-full bg-muted transition-transform',
          'data-[state=checked]:translate-x-[1.125rem] data-[state=checked]:bg-on-accent',
        )}
      />
    </SwitchPrimitive.Root>
  );
}

/** Переключатель с подписью слева и пояснением под ней: строка настроек. */
export function SwitchField({
  label,
  description,
  error,
  checked,
  onCheckedChange,
  disabled,
  inline = false,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  error?: string | null | undefined;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * Фильтр списка («Показать архив»): переключатель и подпись рядом, по ширине содержимого — а не строка
   * настройки с переключателем у дальнего края.
   */
  inline?: boolean;
  className?: string;
}) {
  const id = useId();
  if (inline) {
    return (
      <div className={cn('inline-flex w-fit items-center gap-2', className)}>
        <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-small font-semibold whitespace-nowrap',
            disabled && 'cursor-not-allowed text-muted',
          )}
        >
          {label}
        </label>
      </div>
    );
  }
  return (
    <div className={cn('flex items-start justify-between gap-3', className)}>
      <div className="flex min-w-0 flex-col">
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-body font-semibold',
            disabled && 'cursor-not-allowed text-muted',
          )}
        >
          {label}
        </label>
        {description ? (
          <p id={`${id}-description`} className="text-caption text-muted">
            {description}
          </p>
        ) : null}
        <FieldErrorNote id={`${id}-error`}>{error}</FieldErrorNote>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="mt-0.5"
        aria-describedby={
          [description ? `${id}-description` : null, error ? `${id}-error` : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        aria-invalid={error ? true : undefined}
      />
    </div>
  );
}

export interface OptionCardsProps<V extends string> {
  readonly value: V | null;
  readonly onValueChange: (value: V) => void;
  readonly options: readonly ChoiceOption<V>[];
  /** Колонок на широком экране; на телефоне — одна. */
  readonly columns?: 1 | 2 | 3;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

const COLUMNS = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' } as const;

/** Карточки-варианты: выбор, где каждому варианту нужно пояснение (тип цены, налоговый режим). */
export function OptionCards<V extends string>({
  value,
  onValueChange,
  options,
  columns = 2,
  disabled,
  className,
  ...aria
}: OptionCardsProps<V>) {
  const control = useFieldGroupControl();
  return (
    <RadioGroupPrimitive.Root
      {...control}
      {...aria}
      value={value ?? ''}
      onValueChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onValueChange(option.value);
      }}
      disabled={disabled ?? control.disabled}
      loop
      className={cn('grid gap-2', COLUMNS[columns], className)}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            'group/card relative flex items-start gap-2.5 rounded-lg border-thick border-border bg-surface p-3 text-left',
            'transition-colors hover:border-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            'data-[state=checked]:border-strong data-[state=checked]:shadow-[inset_0_0_0_1px_var(--g-strong)]',
            'disabled:cursor-not-allowed disabled:opacity-45',
          )}
        >
          {option.icon ? (
            <span className="grid size-8 shrink-0 place-items-center rounded-md bg-surface-2 [&_svg]:size-4">
              {option.icon}
            </span>
          ) : null}
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-bold">{option.label}</span>
            {option.description ? (
              <span className="text-caption text-muted">{option.description}</span>
            ) : null}
          </span>
          <span
            aria-hidden="true"
            className={cn(
              'grid size-5 shrink-0 place-items-center rounded-full border-thick border-border text-transparent',
              'group-data-[state=checked]/card:border-accent group-data-[state=checked]/card:bg-accent group-data-[state=checked]/card:text-on-accent',
            )}
          >
            <Check className="size-3" strokeWidth={3} />
          </span>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}

export interface OptionCardsFieldProps<V extends string>
  extends FieldPart, Omit<OptionCardsProps<V>, 'disabled' | 'className'> {}

export function OptionCardsField<V extends string>({
  value,
  onValueChange,
  options,
  columns,
  ...field
}: OptionCardsFieldProps<V>) {
  return (
    <Field {...field}>
      <OptionCards
        value={value}
        onValueChange={onValueChange}
        options={options}
        {...(columns ? { columns } : {})}
      />
    </Field>
  );
}
